/**
 * PerinatalAutoSuggestCard.jsx — iter1108 (Feb 2026)
 *
 * Reads the user's profile signals (LMP, delivery date, pregnancy flag,
 * breastfeeding mode) and asks the backend to suggest a perinatal stage.
 * User can:
 *   • Update inputs inline (delivery date, breastfeeding mode) — auto refetch.
 *   • Tap „Приемам" → PATCH profile.perinatal.stage → refreshUser.
 *   • Tap „Ръчна селекция" → deep-link to full picker in ProfileEditModal.
 *
 * Renders only for female users. Mounted from `ProfilePageFull.jsx` next
 * to `CyclePhaseCard`.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Baby, Sparkles, Check, ChevronRight, Loader2, Info } from "lucide-react";
import { useAuth } from "@/App";
import { useTheme } from "@/theme/ThemeContext";
import { useLanguage } from "@/i18n/LanguageContext";

const CONFIDENCE_STYLE = {
  high:   { icon: "🟢", label_bg: "Висока",   label_en: "High",   ring: "ring-emerald-300" },
  medium: { icon: "🟡", label_bg: "Средна",   label_en: "Medium", ring: "ring-amber-300"   },
  low:    { icon: "🟠", label_bg: "Ниска",    label_en: "Low",    ring: "ring-orange-300"  },
  none:   { icon: "⚪", label_bg: "Няма",     label_en: "None",   ring: "ring-slate-300"   },
};

const STAGE_LABEL_BG = {
  preconception:       "Подготовка за бременност",
  pregnancy_t1:        "Бременност — I триместър",
  pregnancy_t2:        "Бременност — II триместър",
  pregnancy_t3:        "Бременност — III триместър",
  postpartum:          "Постпартум (без кърмене)",
  lactation_exclusive: "Кърмене — изключително",
  lactation_partial:   "Кърмене — частично",
};

export default function PerinatalAutoSuggestCard() {
  const { API, user, refreshUser } = useAuth();
  const { isDark } = useTheme();
  const { language } = useLanguage();
  const lang = language === "en" ? "en" : "bg";
  const bg = lang === "bg";

  const p = user?.profile || {};
  const perinatal = p.perinatal || {};
  const gender = (p?.personal?.gender || "").toLowerCase();
  const isFemale = gender.startsWith("f") || gender === "female" || gender === "жена";

  const [suggestion, setSuggestion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showInputs, setShowInputs] = useState(false);
  const [deliveryDate, setDeliveryDate] = useState(perinatal.delivery_date || "");
  const [lactationMode, setLactationMode] = useState(perinatal.lactation_mode || "");
  const [pregnant, setPregnant] = useState(!!(perinatal.pregnant || p?.personal?.pregnant));
  const [pregnancyWeek, setPregnancyWeek] = useState(perinatal.pregnancy_week || "");

  const fetchSuggestion = useCallback(async () => {
    if (!isFemale || !API) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/perinatal-dil/auto-suggest`);
      setSuggestion(data);
    } catch {
      setSuggestion(null);
    } finally {
      setLoading(false);
    }
  }, [API, isFemale]);

  useEffect(() => { fetchSuggestion(); }, [fetchSuggestion]);

  const saveInputsAndRefetch = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const patch = { profile: { perinatal: {} } };
      if (deliveryDate)  patch.profile.perinatal.delivery_date = deliveryDate;
      if (lactationMode) patch.profile.perinatal.lactation_mode = lactationMode;
      if (pregnancyWeek) patch.profile.perinatal.pregnancy_week = Number(pregnancyWeek);
      patch.profile.personal = { pregnant };
      await axios.patch(`${API}/auth/me`, patch);
      toast.success(bg ? "Записано ✓" : "Saved ✓");
      await refreshUser?.();
      await fetchSuggestion();
    } catch (e) {
      toast.error(bg ? "Грешка при запис" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const acceptSuggestion = async () => {
    if (!suggestion?.stage || saving) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/auth/me`, {
        profile: { perinatal: { stage: suggestion.stage } },
      });
      toast.success(bg
        ? `Етапът е приет: ${STAGE_LABEL_BG[suggestion.stage] || suggestion.stage}`
        : `Stage accepted: ${suggestion.stage}`);
      await refreshUser?.();
    } catch (e) {
      toast.error(bg ? "Грешка при запис" : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (!isFemale) return null;

  const currentStage = perinatal.stage;
  const conf = CONFIDENCE_STYLE[suggestion?.confidence || "none"];
  const cardCls = isDark
    ? "bg-gradient-to-br from-pink-950/25 via-slate-900/40 to-fuchsia-900/15 border-pink-500/30"
    : "bg-gradient-to-br from-pink-50 via-white to-fuchsia-50 border-pink-200";

  return (
    <section
      className={`rounded-2xl border p-4 mt-4 ${cardCls}`}
      data-testid="perinatal-auto-suggest-card"
    >
      <div className="flex items-start gap-2 mb-3">
        <Baby className={`w-4 h-4 mt-0.5 shrink-0 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
        <div className="min-w-0 flex-1">
          <div className={`text-sm font-extrabold flex items-center gap-2 flex-wrap ${isDark ? "text-white" : "text-slate-900"}`}>
            {bg ? "🤰 Перинатален етап — авто-препоръка" : "🤰 Perinatal stage — auto-suggest"}
          </div>
          <p className={`text-[11px] mt-0.5 leading-snug ${isDark ? "text-slate-400" : "text-slate-600"}`}>
            {bg
              ? "На база последна менструация и раждане препоръчваме етап без ръчна селекция."
              : "Based on your LMP + delivery date we suggest a stage automatically."}
          </p>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className={`text-[11px] flex items-center gap-1.5 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
          <Loader2 className="w-3 h-3 animate-spin" />
          {bg ? "Изчислявам…" : "Computing…"}
        </div>
      )}

      {/* Suggestion display */}
      {!loading && suggestion && (
        <div
          className={`rounded-xl p-3 border ${
            isDark ? "bg-slate-900/50 border-slate-700/60" : "bg-white border-slate-200"
          }`}
          data-testid="perinatal-auto-suggest-result"
        >
          <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className={`text-[10px] uppercase tracking-wider font-bold ${isDark ? "text-pink-300" : "text-pink-700"}`}>
                {bg ? "Препоръчан етап" : "Suggested stage"}
              </div>
              <div
                className={`text-base font-extrabold ${isDark ? "text-white" : "text-slate-900"}`}
                data-testid="perinatal-auto-suggest-stage"
              >
                {suggestion.stage
                  ? (bg ? STAGE_LABEL_BG[suggestion.stage] || suggestion.stage : suggestion.stage)
                  : (bg ? "Няма препоръка" : "No suggestion")}
              </div>
            </div>
            <span
              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ring-1 ${conf.ring} ${
                isDark ? "bg-slate-800/70 text-white" : "bg-white text-slate-700"
              }`}
              data-testid="perinatal-auto-suggest-confidence"
            >
              {conf.icon} {bg ? conf.label_bg : conf.label_en}
            </span>
          </div>

          {/* Rationale */}
          <div className={`text-[11px] leading-snug mb-3 ${isDark ? "text-slate-300" : "text-slate-700"}`}>
            <Info className="inline w-3 h-3 mr-1 align-text-top opacity-70" />
            {bg ? suggestion.rationale_bg : suggestion.rationale_en}
          </div>

          {/* Currently active stage — for reassurance */}
          {currentStage && (
            <div className={`text-[10px] mb-2 ${isDark ? "text-slate-400" : "text-slate-500"}`}>
              {bg ? "Текущ етап в профила: " : "Current stage on profile: "}
              <span className="font-bold">
                {bg ? STAGE_LABEL_BG[currentStage] || currentStage : currentStage}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-2">
            {suggestion.stage && suggestion.stage !== currentStage && (
              <button
                type="button"
                onClick={acceptSuggestion}
                disabled={saving}
                data-testid="perinatal-auto-suggest-accept"
                className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition ${
                  isDark ? "bg-pink-500 hover:bg-pink-400 text-white" : "bg-pink-600 hover:bg-pink-700 text-white"
                } disabled:opacity-60`}
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                {bg ? "Приемам" : "Accept"}
              </button>
            )}
            {suggestion.stage && suggestion.stage === currentStage && (
              <span
                className={`inline-flex items-center gap-1 text-[10.5px] font-bold px-2 py-1 rounded-full ${
                  isDark ? "bg-emerald-500/20 text-emerald-200" : "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200"
                }`}
                data-testid="perinatal-auto-suggest-in-sync"
              >
                <Check className="w-3 h-3" /> {bg ? "В синхрон" : "In sync"}
              </span>
            )}
            <button
              type="button"
              onClick={() => setShowInputs((s) => !s)}
              data-testid="perinatal-auto-suggest-refine"
              className={`inline-flex items-center gap-1 text-[10.5px] font-semibold underline ${
                isDark ? "text-slate-300 hover:text-white" : "text-slate-700 hover:text-slate-900"
              }`}
            >
              <Sparkles className="w-3 h-3" />
              {bg ? "Прецизирай сигналите" : "Refine signals"}
            </button>
            <a
              href="/profile?edit=1&tab=body"
              data-testid="perinatal-auto-suggest-manual"
              className={`inline-flex items-center gap-0.5 text-[10.5px] font-semibold ${
                isDark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              {bg ? "Ръчна селекция" : "Manual pick"}
              <ChevronRight className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}

      {/* Refine inputs */}
      {showInputs && (
        <div
          className={`rounded-xl p-3 border mt-3 ${
            isDark ? "bg-slate-900/60 border-slate-700/50" : "bg-white border-slate-200"
          }`}
          data-testid="perinatal-auto-suggest-inputs"
        >
          <div className={`text-[10px] uppercase tracking-wider font-bold mb-2 ${isDark ? "text-pink-300" : "text-pink-700"}`}>
            {bg ? "Прецизирай сигналите" : "Refine signals"}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <label className="flex flex-col gap-0.5">
              <span className={`text-[10px] font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {bg ? "Дата на раждане (последно)" : "Delivery date (last)"}
              </span>
              <input
                type="date"
                value={deliveryDate}
                onChange={(e) => setDeliveryDate(e.target.value)}
                data-testid="perinatal-auto-input-delivery"
                className={`text-[12px] px-2 py-1.5 rounded border outline-none transition-colors ${
                  isDark ? "bg-slate-900 border-slate-600 text-white focus:border-pink-400"
                         : "bg-white border-slate-300 text-slate-900 focus:border-pink-500"
                }`}
              />
            </label>
            <label className="flex flex-col gap-0.5">
              <span className={`text-[10px] font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                {bg ? "Кърмене" : "Breastfeeding"}
              </span>
              <select
                value={lactationMode}
                onChange={(e) => setLactationMode(e.target.value)}
                data-testid="perinatal-auto-input-lactation"
                className={`text-[12px] px-2 py-1.5 rounded border outline-none transition-colors ${
                  isDark ? "bg-slate-900 border-slate-600 text-white focus:border-pink-400"
                         : "bg-white border-slate-300 text-slate-900 focus:border-pink-500"
                }`}
              >
                <option value="">{bg ? "— не се отнася —" : "— not applicable —"}</option>
                <option value="exclusive">{bg ? "Изключително" : "Exclusive"}</option>
                <option value="partial">{bg ? "Частично" : "Partial"}</option>
                <option value="weaning">{bg ? "Отбиване" : "Weaning"}</option>
              </select>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-2">
            <label className={`flex items-center gap-2 px-2 py-1.5 rounded border cursor-pointer text-[11px] ${
              isDark ? "bg-slate-900 border-slate-600 text-slate-200" : "bg-white border-slate-300 text-slate-700"
            }`}>
              <input
                type="checkbox"
                checked={pregnant}
                onChange={(e) => setPregnant(e.target.checked)}
                data-testid="perinatal-auto-input-pregnant"
              />
              {bg ? "В момента съм бременна" : "Currently pregnant"}
            </label>
            {pregnant && (
              <label className="flex flex-col gap-0.5">
                <span className={`text-[10px] font-semibold ${isDark ? "text-slate-300" : "text-slate-600"}`}>
                  {bg ? "Гестационна седмица" : "Gestational week"}
                </span>
                <input
                  type="number"
                  min="1"
                  max="42"
                  value={pregnancyWeek}
                  onChange={(e) => setPregnancyWeek(e.target.value)}
                  data-testid="perinatal-auto-input-week"
                  className={`text-[12px] px-2 py-1.5 rounded border outline-none transition-colors ${
                    isDark ? "bg-slate-900 border-slate-600 text-white focus:border-pink-400"
                           : "bg-white border-slate-300 text-slate-900 focus:border-pink-500"
                  }`}
                />
              </label>
            )}
          </div>

          <button
            type="button"
            onClick={saveInputsAndRefetch}
            disabled={saving}
            data-testid="perinatal-auto-input-save"
            className={`inline-flex items-center gap-1.5 text-[11px] font-bold px-3 py-1.5 rounded-full transition ${
              isDark ? "bg-pink-500 hover:bg-pink-400 text-white" : "bg-pink-600 hover:bg-pink-700 text-white"
            } disabled:opacity-60`}
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            {bg ? "Запази и препоръчай отново" : "Save & re-suggest"}
          </button>
        </div>
      )}

      <p className={`text-[9.5px] italic mt-2 ${isDark ? "text-slate-500" : "text-slate-500"}`}>
        {bg
          ? "Образователно съдържание. Не заменя лекарска консултация."
          : "Educational content. Does not replace medical consultation."}
      </p>
    </section>
  );
}
